import { BaseComponent } from '../../shared/components/base.component';
import { Component, OnInit, AfterViewInit, ViewChild, ElementRef, Input } from '@angular/core';
import { AuthenticationService } from 'src/core/http/authentication.service';
import { Router, ActivatedRoute } from '@angular/router';
import { FormBuilder, FormGroup, Validators, AbstractControl } from '@angular/forms';
import { AppService } from 'src/core/services/app-service';
import { envSettings, ProviderTypeEnum } from 'src/common/constant';
import { CustomValidators } from 'src/app/shared/custom-validators';
import * as moment from 'moment-timezone';
import { MatSort } from '@angular/material/sort';
import { MatTable } from '@angular/material/table';
import { MatAutocomplete, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { ReportService } from 'src/core/http/report.service';
import { GenericDataSource } from 'src/core/http/generic.datasource';
import { takeUntil, startWith, delay, tap } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { CdkDetailRowDirective } from './cdk-detail-row.directive';
import { saveAs } from 'file-saver';
import { ProviderService } from 'src/core/http/provider.service';
import { FacilityService } from 'src/core/http/facility.service';

@Component({
  selector: 'app-encounter-report',
  templateUrl: './encounter-report.component.html',
  styleUrls: ['./encounter-report.component.scss'],
  animations: [
    trigger('detailExpand', [
      state('void', style({ height: '0px', minHeight: '0', visibility: 'hidden' })),
      state('*', style({ height: '*', visibility: 'visible' })),
      transition('void <=> *', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)'))
    ])
  ]
})
export class EncounterReportComponent extends BaseComponent implements OnInit, AfterViewInit {
  private openedRow: CdkDetailRowDirective;
  public enviOptions = envSettings;
  @Input() singleChildRowDetail = true;
  disableExportButton = false;
  filterForm: FormGroup;
  filteredDoctors: any[] = [];
  pageSize = 40;
  @ViewChild('doctorAuto') doctorAutocompleteRef: MatAutocomplete;
  @ViewChild('doctorInput', { read: MatAutocompleteTrigger }) doctorAutocompleteTrigger: MatAutocompleteTrigger;
  @ViewChild('inputSearch', { static: true }) inputSearch: ElementRef;
  @ViewChild(MatTable, { static: true }) table: MatTable<any>;
  @ViewChild(MatSort, { static: true }) sort: MatSort;
  columnDefinitions = [
    { columnName: 'RM', show: true },
    { columnName: 'DOS', show: true },
    { columnName: 'PatientName', show: true },
    { columnName: 'DOB', show: true },
    { columnName: 'ATTN', show: true },
    { columnName: 'MLProvider', show: true },
    { columnName: 'ATTN_1', show: false },
    { columnName: 'CM', show: false },
    { columnName: 'LOS', show: true },
    { columnName: 'Procedure', show: true },
    { columnName: 'Diagnoses', show: true },
    { columnName: 'Supplemental', show: true },
    { columnName: 'AvoidableReasons', show: false },
    { columnName: 'Handoff', show: true }
  ];
  mainColumnDefinitions = [{ columnName: 'FacilityCode', show: true }];

  dataSource: GenericDataSource;

  getDisplayedColumns(): string[] {
    return this.columnDefinitions.filter(cd => cd.show).map(cd => cd.columnName);
  }

  getDisplayedMainColumns(): string[] {
    return this.mainColumnDefinitions.filter(cd => cd.show).map(cd => cd.columnName);
  }
  isExpansionDetailRow = (index, row) => row.hasOwnProperty('detailRow');

  onToggleChange(cdkDetailRow: CdkDetailRowDirective): void {
    if (this.singleChildRowDetail && this.openedRow && this.openedRow.expended) {
      this.openedRow.toggle();
    }
    this.openedRow = cdkDetailRow.expended ? cdkDetailRow : cdkDetailRow;
  }

  constructor(
    protected authService: AuthenticationService,
    private router: Router,
    private formBuilder: FormBuilder,
    private route: ActivatedRoute,
    public appService: AppService,
    private providerService: ProviderService,
    private reportService: ReportService,
    private facilityService: FacilityService,

  ) {
    super(authService);
    this.enviOptions.headerSectionTitle = 'Report';
    this.enviOptions.headerSectionIcon = { iconLibrary: 'fontawesome', iconName: 'fas fa-file-contract' };
  }

  ngOnInit() {
    this.filterForm = this.formBuilder.group({
      searchInput: [''],
      fromDate: [''],
      toDate: [''],
      doctorFilter: this.formBuilder.group({
        inputControl: ['', [CustomValidators.requiredObject(true)]]
      }),
      facilityFilter: this.formBuilder.group({
        inputControl: ['', [CustomValidators.requiredObject(true)]]
      })
    });

    const today = moment().startOf('day');
    this.filterFormControls.fromDate.setValue(today);
    this.filterFormControls.toDate.setValue(today.endOf('day'));
    this.dataSource = new GenericDataSource(this.getReport);
    this.dataSource.isLoading$.pipe(takeUntil(this.unsubscribe)).subscribe(loadingYN => {
      this.appService.setIsLoading(loadingYN);
    });
  }

  ngAfterViewInit() {
    // https://blog.angular-university.io/angular-debugging/
    setTimeout(() => {
      this.dataSource.showNextPage(false, null, this.pageSize);
      this.dataSource.isLoading$.pipe(takeUntil(this.unsubscribe)).subscribe(loadingYN => {
        this.appService.setIsLoading(loadingYN);
      });
    });
  }
  get filterFormControls() {
    return this.filterForm.controls;
  }

  filterView() {
    if (this.filterForm.valid) {
      this.dataSource.showNextPage(false, null, this.pageSize);
    } else {
      CustomValidators.validateAllFormFields(this.filterForm);
    }
  }

  getDoctors = (currentIndex: number, pageSize: number, query: string) => {
    return this.getProviders(currentIndex, pageSize, 'Initials ASC', query, ProviderTypeEnum.Doctor); // 1001 = Doctor
  }

  private getProviders = (currentIndex: number, pageSize: number, sorting: string, query: string, providerTypeEnum: number) => {
    return this.providerService.getProviders(currentIndex, pageSize, sorting, query, providerTypeEnum);
  }

  getCodes = (currentIndex: number, pageSize: number, query: string) => {
    return this.facilityService.getFacilities(currentIndex, pageSize, 'Initials ASC', query);
  }

  private getFacilities = (currentIndex: number, pageSize: number, sorting: string, query: string) => {
    return this.facilityService.getFacilities(currentIndex, pageSize, sorting, query);
  }

  setFilterDate(dateLabel: string) {
    const today = moment().startOf('day');
    switch (dateLabel) {
      case 'today':
        this.filterFormControls.fromDate.setValue(today);
        this.filterFormControls.toDate.setValue(today.endOf('day'));
        break;
      case '-1':
        const yesterday = today.add(-1, 'days');
        this.filterFormControls.fromDate.setValue(yesterday);
        this.filterFormControls.toDate.setValue(yesterday.endOf('day'));
        break;
      case '+1':
        const tomorrow = today.add(1, 'days');
        this.filterFormControls.fromDate.setValue(tomorrow);
        this.filterFormControls.toDate.setValue(tomorrow.endOf('day'));
        break;
    }
  }

  getReport = (currentIndex: number, pageSize: number) => {
    const filterParams = {
      // fromDate: this.filterFormControls.fromDate.value ? this.datePipe.transform(this.filterFormControls.fromDate.value, 'yyyy-MM-dd') : null,
      // toDate: this.filterFormControls.toDate.value ? this.datePipe.transform(this.filterFormControls.toDate.value, 'yyyy-MM-dd') : null,
      fromDate: this.filterFormControls.fromDate.value ? this.filterFormControls.fromDate.value.startOf('day').format('YYYY-MM-DD') : null,
      toDate: this.filterFormControls.toDate.value ? this.filterFormControls.toDate.value.endOf('day').format('YYYY-MM-DD') : null,
      authUserDoctorId: this.filterFormControls.doctorFilter.get('inputControl').value
        ? this.filterFormControls.doctorFilter.get('inputControl').value.AuthUserId
        : null,
        facilityId: this.filterFormControls.facilityFilter.get('inputControl').value
        ? this.filterFormControls.facilityFilter.get('inputControl').value.FacilityId
        : null
    };
    return this.reportService.GetFacilities(currentIndex, pageSize, `${this.sort.active} ${this.sort.direction}`, null, filterParams);
  }

  trackByFn(index, item) {
    return item.SrNo;
  }

  exportToExcel() {
    this.disableExportButton = true;
    const filterParams = {
      fromDate: this.filterFormControls.fromDate.value ? this.filterFormControls.fromDate.value.startOf('day').toISOString() : null,
      toDate: this.filterFormControls.toDate.value ? this.filterFormControls.toDate.value.endOf('day').toISOString() : null,
      authUserDoctorId: this.filterFormControls.doctorFilter.get('inputControl').value
        ? this.filterFormControls.doctorFilter.get('inputControl').value.AuthUserId
        : null,
        facilityId: this.filterFormControls.facilityFilter.get('inputControl').value
        ? this.filterFormControls.facilityFilter.get('inputControl').value.FacilityId
        : null
    };
    this.reportService.GetFacilitiesExcel(0, 100, `${this.sort.active} ${this.sort.direction}`, null, filterParams).subscribe(
      blob => {
        saveAs(blob, 'FacilityReport.xlsx');
        this.disableExportButton = false;
      },
      errData => {
        this.disableExportButton = false;
      }
    );
  }
}
